/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RequestScope } from '../../../actions/api';
import { FormGroup, Input } from 'reactstrap';
import { Title } from './DataShareForm';
import { RequestScopeExtended } from '../../../utils/requestScope';
import { BasicAlert } from '../../alert/BasicAlert';
import { useIsOverflow } from '../../../hooks/useIsOverflow';
import './DataScopeGroups.scss';

interface DataScopeGroupsParams {
  htmlId: string;
  title?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  isFullAccess: boolean;
  requestScopes: RequestScope[];
  handleChangePacks: (scopes: RequestScope[]) => Promise<void>,
  /** Scrollable container max heigh **/
  scrollContainMaxHeight?: `${number}px`,
  /** When shadow for scrollable container shuold disappear (scrolled from top **/
  hideShadowFromTop?: number;
  initiativeRequestScopes: RequestScopeExtended[];
}

const shadowClassName = 'off-bottom';

export function DataScopeGroups(props: DataScopeGroupsParams) {
  const {
    title = 'Include data from',
    isFullAccess,
    handleChangePacks,
    htmlId,
    onChange,
    requestScopes,
    scrollContainMaxHeight = '250px',
    hideShadowFromTop = 160,
    initiativeRequestScopes
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const [shadowClass, setShadowClass] = useState('')
  const isOverflow = useIsOverflow(ref);
  const [message, setMessage] = useState('');

  useLayoutEffect(() => {
    // Update on layout change
    setShadowClass(isOverflow ? shadowClassName : '')
  }, [isOverflow]);

  useEffect(() => {
    if (isFullAccess) {
      setMessage('');
    }
  }, [isFullAccess]);

  const onScopeChange = useCallback((selectedOption: RequestScope) => {
      const selectedScopes = requestScopes.map(r => r.code);
      const scopeId = selectedOption?.code;

      const newSelectedScopes = selectedScopes.includes(scopeId)
        ? selectedScopes.filter((code) => code !== scopeId)
        : [...selectedScopes, scopeId];
      const validScopes = initiativeRequestScopes.filter((item) => newSelectedScopes.includes(item.code));

      if (newSelectedScopes.length === 0) {
        setMessage('At least one survey pack is required');
        return;
      }
      setMessage('');

      handleChangePacks(validScopes).catch(e => setMessage(e.message));
    },
    [handleChangePacks, initiativeRequestScopes, requestScopes]
  );

  return (
    <>
      <Title>{title}</Title>
      <FormGroup switch className='d-flex align-items-center mt-2'>
        <Input id={`${htmlId}-all-packs`} type='switch' className='mr-2' onChange={onChange} checked={isFullAccess} />
        <label htmlFor={`${htmlId}-all-packs`}>All survey packs</label>
      </FormGroup>
      <div className={'pr-3'}>
        <div className={`mt-3 scope-group-container ${shadowClass}`}>
          <BasicAlert hide={isFullAccess} type={'warning'}>
            {message}
          </BasicAlert>

          <div
            onScroll={(event) => {
              const target = event.currentTarget;
              if (target.scrollTop < hideShadowFromTop && isOverflow) {
                setShadowClass(shadowClassName);
              } else {
                setShadowClass('');
              }
            }}
            ref={ref}
            style={{ maxHeight: scrollContainMaxHeight }}
            className='scroll-container pl-1 mt-2 text-ThemeTextMedium'
          >
            {initiativeRequestScopes.map((requestScope) => (
              <div key={requestScope.code} className='d-flex gap-2 mt-1'>
                <Input
                  type='checkbox'
                  disabled={isFullAccess}
                  onChange={() => onScopeChange(requestScope)}
                  checked={isFullAccess || requestScopes.some((r) => r.code === requestScope.code)}
                  className='share-scope-checkbox'
                />
                <label>{requestScope.name}</label>
              </div>
            ))}
          </div>
          {isOverflow ? <div className='shadows' aria-hidden='true' /> : null}
        </div>
        <div className={'ml-1 mt-2 text-ThemeTextLight text-xs'}>
          (
          {isFullAccess || requestScopes.length > initiativeRequestScopes.length
            ? initiativeRequestScopes.length
            : requestScopes.length}{' '}
          / {initiativeRequestScopes.length})
        </div>
      </div>
    </>
  );
}
